/** Stages in which a workstream's data can still be changed. */
export const FOUNDATION_EDITABLE_STAGES = [
  "START",
  "PENDING",
  "AI_QUEUE",
  "EXPERT_REVIEW",
  "IN_PROGRESS",
  "EXCLUDED",
  "EXCLUDED_BY_RULE",
];

/**
 * Whether this user may change one workstream's sources, figures and notes — and if not, why.
 *
 * Three conditions, and all of them have to hold:
 *
 *  - the caller holds the backend's write authority (`update:organization-questionnaires-data`). v1's rule
 *    checks only stage and assignment, which is not enough here: an ORG_VIEWER passes both and is then
 *    refused by the server.
 *  - the stage still allows editing, with one exception — an INTERNAL_REVIEW question stays editable for its
 *    OWNER, who is the person being asked to review it.
 *  - the caller is the owner or an assignee.
 *
 * Returns the reason as well as the verdict, because a read-only page with no explanation is
 * indistinguishable from a permissions bug. The reason names the condition that actually failed rather than
 * listing every possibility.
 *
 * @param {object|null} question the year's ISSB question
 * @param {{hasWriteRole: boolean, isOwner: Function, isAssigned: Function}} caller
 * @returns {{canEdit: boolean, reason: (string|null)}}
 */
export function foundationYearEditability(question, { hasWriteRole, isOwner, isAssigned } = {}) {
  if (!question) return { canEdit: false, reason: null };

  if (!hasWriteRole) {
    return { canEdit: false, reason: "Your role cannot change foundation data." };
  }

  const stageAllows =
    FOUNDATION_EDITABLE_STAGES.includes(question.stageType) ||
    (question.stageType === "INTERNAL_REVIEW" && isOwner?.(question));
  if (!stageAllows) {
    return {
      canEdit: false,
      reason: `This is in ${question.stage || "a later"} stage, past the editing stages.`,
    };
  }

  if (!isAssigned?.(question)) {
    return { canEdit: false, reason: "You are not the owner or an assignee of this." };
  }

  return { canEdit: true, reason: null };
}
